import type { FC, MouseEventHandler, ReactNode } from "react";
import { CONTROL_HEIGHT, ICON_SIZE } from "@/theme/tokens";
import SystemButton from "../SystemButton/SystemButton";

// One box, one glyph, one hover for every action under an answer — the size is what makes copy, regenerate
// and the ratings read as a single row instead of three widgets that happen to be adjacent.
const microButtonSx = {
	width: CONTROL_HEIGHT.MICRO,
	height: CONTROL_HEIGHT.MICRO,
	minWidth: CONTROL_HEIGHT.MICRO,
	"& .MuiSvgIcon-root": { fontSize: ICON_SIZE.MICRO },
};

type Props = {
	/** Accessible name + tooltip, e.g. "Copy answer". Already translated. */
	label: string;
	icon: ReactNode;
	/**
	 * Receives the click event, so an action inside a clickable surface can `stopPropagation()` (and read
	 * `currentTarget` as the anchor for whatever it opens). A plain `() => void` still assigns fine.
	 */
	onClick: MouseEventHandler<HTMLButtonElement>;
	/**
	 * Toggle state for an action that stays on once chosen (a rating): tints the glyph and publishes
	 * `aria-pressed`. Omit entirely for one-shot actions — they are not toggles.
	 */
	active?: boolean;
	loading?: boolean;
	disabled?: boolean;
	/**
	 * Tooltip override; defaults to `label`. Pass it to say WHY the action is unavailable — a disabled
	 * button swallows pointer events, and `SystemButton` already wraps a tooltipped button in the span
	 * that lets hover register, so the explanation reaches the user rather than the action just going dead.
	 */
	tooltip?: ReactNode;
	/**
	 * Take the icon out of the tab order with `-1` — for a bar inside a composite control that is itself
	 * the single tab stop and reaches these actions by keyboard shortcut instead (see the monthly day
	 * cell). Omit it and the icon is a normal tab stop.
	 */
	tabIndex?: number;
	dataTestId?: string;
};

/**
 * One action in a `ChatActionBar` — a micro, borderless icon button sized for sitting inside a chat
 * answer rather than in page chrome. Generic over any chat surface; the caller owns the icon and the
 * behavior, this owns the size, tint and hit area.
 */
export const ChatActionButton: FC<Props> = ({ label, icon, onClick, active, loading, disabled, tooltip, tabIndex, dataTestId }) => (
	<SystemButton
		buttonVariant="borderless"
		iconButton
		touchTarget
		tooltip={tooltip ?? label}
		tabIndex={tabIndex}
		aria-label={label}
		aria-pressed={active}
		loading={loading}
		disabled={disabled}
		onClick={onClick}
		data-testid={dataTestId}
		sx={active === true ? { ...microButtonSx, color: "primary.main" } : microButtonSx}
	>
		{icon}
	</SystemButton>
);
